import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShield, FiAlertTriangle, FiEdit3, FiDownload, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import Cpc25BarChart from './shared/Cpc25BarChart';
import Cpc25DonutChart from './shared/Cpc25DonutChart';
import useCpc25Report from '../../../hooks/useCpc25Report';
import { buildCpc25Csv, cpc25CsvFilename } from './shared/cpc25Export';
import { provisionByEntity, casesByClassification } from './shared/cpc25Charts';
import type {
    Cpc25AttentionCase,
    Cpc25Report,
    Cpc25ReportEntity,
    Cpc25ReportPole,
} from '../../../types/LegalCase';

// Relatório consolidado CPC 25 (Jurídico → Relatório CPC 25). Equivalente calculado da aba
// VALORES da planilha: por entidade credora, Ativo/Passivo lado a lado, tabela Classificação ×
// [Nº, Perda estimada, Provisão] + totais, mais gráficos de provisão por entidade e de
// distribuição de casos por classificação. Read-only, com exportação CSV. A provisão já vem
// calculada pela regra do CPC 25 no backend (só passivo provável).

// Dispara o download do CSV a partir de uma string (Blob + link temporário). Fora do componente.
const downloadCsv = (content: string, filename: string): void => {
    // BOM para o Excel abrir com acentuação correta.
    const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Uma tabela de polo (classificações + subtotais). Fora do componente de página.
const PoleTable = ({ pole }: { pole: Cpc25ReportPole }) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{pole.poleFormatted}</span>
        </div>
        <table className="w-full text-sm">
            <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400">
                    <th className="py-2 px-3 text-left font-medium">Classificação</th>
                    <th className="py-2 px-3 text-right font-medium">Nº</th>
                    <th className="py-2 px-3 text-right font-medium">Perda estimada</th>
                    <th className="py-2 px-3 text-right font-medium">Provisão</th>
                </tr>
            </thead>
            <tbody>
                {pole.rows.map((row) => (
                    <tr key={`${pole.pole}-${row.classification ?? 'none'}`}
                        className="border-t border-gray-100 dark:border-slate-700">
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                            <span className="flex items-center gap-1.5">
                                {row.classificationFormatted}
                                {row.offSuggestion > 0 && (
                                    <FiAlertTriangle className="text-amber-500" size={12}
                                        title={`${row.offSuggestion} caso(s) com percentual fora do padrão`} />
                                )}
                                {row.overridden > 0 && (
                                    <FiEdit3 className="text-gray-400" size={12}
                                        title={`${row.overridden} caso(s) com provisão ajustada manualmente`} />
                                )}
                            </span>
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">{row.count}</td>
                        <td className="py-2 px-3 text-right dark:text-gray-200">{row.estimatedLossFormatted || '—'}</td>
                        <td className="py-2 px-3 text-right font-medium dark:text-white">{row.provisionFormatted || 'R$ 0,00'}</td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 font-medium">
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-200">Total {pole.poleFormatted}</td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{pole.count}</td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{pole.estimatedLossFormatted || '—'}</td>
                    <td className="py-2 px-3 text-right text-gray-900 dark:text-white">{pole.provisionFormatted || 'R$ 0,00'}</td>
                </tr>
            </tfoot>
        </table>
    </div>
);

const EntityBlock = ({ entity }: { entity: Cpc25ReportEntity }) => (
    <div className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{entity.creditorEntity}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {entity.poles.map((pole) => (
                <PoleTable key={`${entity.creditorEntity}-${pole.pole}`} pole={pole} />
            ))}
        </div>
    </div>
);

// Card de total destacado. Cor de destaque opcional (usada na provisão) e tooltip explicativo.
const SummaryCard = ({ label, value, accent, hint }: { label: string; value: string; accent?: 'danger' | 'default'; hint?: string }) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4" title={hint}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-2xl font-semibold ${accent === 'danger'
            ? 'text-red-700 dark:text-red-300'
            : 'text-gray-900 dark:text-white'}`}>
            {value}
        </p>
    </div>
);

// Motivo textual pelo qual o caso merece atenção. Fora do componente.
const attentionReason = (c: Cpc25AttentionCase): string => {
    if (c.offSuggestion && c.overridden) return 'Percentual fora do padrão + provisão ajustada';
    if (c.overridden) return 'Provisão ajustada manualmente';
    return 'Percentual fora do padrão sugerido';
};

// Linha da lista "Casos que merecem atenção".
const AttentionRow = ({ c, onOpen }: { c: Cpc25AttentionCase; onOpen: (id: string) => void }) => (
    <button
        type="button"
        onClick={() => onOpen(c.id)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
    >
        <span className="flex items-center gap-2 min-w-0">
            {c.overridden
                ? <FiEdit3 className="text-gray-400 shrink-0" size={13} />
                : <FiAlertTriangle className="text-amber-500 shrink-0" size={13} />}
            <span className="truncate">
                <span className="font-mono text-xs text-blue-700 dark:text-blue-400">{c.externalCaseNumber || '(sem nº)'}</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs"> • {c.creditorEntity} • {c.poleFormatted} • {c.classificationFormatted}</span>
            </span>
        </span>
        <span className="flex items-center gap-3 shrink-0 text-xs">
            <span className="text-gray-500 dark:text-gray-400" title={c.provisionOverrideReason || undefined}>
                {attentionReason(c)}
                {c.overridden && c.provisionOverrideReason ? ' *' : ''}
            </span>
            <span className="text-gray-400 dark:text-gray-500">{c.estimatedLossPercentFormatted || '—'}</span>
        </span>
    </button>
);

// Seção expansível "Casos que merecem atenção", recolhida por padrão.
const AttentionSection = ({ cases, onOpen }: { cases: Cpc25AttentionCase[]; onOpen: (id: string) => void }) => {
    const [open, setOpen] = useState(false);
    if (cases.length === 0) return null;
    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left"
            >
                {open ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Casos que merecem atenção
                </span>
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                    {cases.length}
                </span>
            </button>
            {open && (
                <div className="px-2 pb-2 space-y-0.5">
                    {cases.map((c) => <AttentionRow key={c.id} c={c} onOpen={onOpen} />)}
                </div>
            )}
        </div>
    );
};

const Cpc25ReportPage = () => {
    const r = useCpc25Report();
    const navigate = useNavigate();
    const [entityInput, setEntityInput] = useState('');

    const report: Cpc25Report = r.report;
    const provisionSegments = useMemo(() => provisionByEntity(report), [report]);
    const classificationSegments = useMemo(() => casesByClassification(report), [report]);

    const hasData = report.entities.length > 0;
    // Há filtro aplicado quando o hook está com uma entidade setada (o que já foi buscado),
    // não quando só há texto digitado ainda não confirmado.
    const hasActiveFilter = r.creditorEntity.trim() !== '';

    const applyFilter = () => r.setCreditorEntity(entityInput);

    const clearFilter = () => {
        setEntityInput('');
        r.setCreditorEntity('');
    };

    const onExport = () => {
        downloadCsv(buildCpc25Csv(report), cpc25CsvFilename());
    };

    return (
        <>
            {r.isLoading && <Loading />}

            <div className="flex flex-col h-screen bg-white dark:bg-slate-800 overflow-hidden">
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <FiShield className="text-blue-600" size={20} />
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Relatório CPC 25</h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-56">
                                <TextEdit
                                    label="Filtrar por entidade (ex.: holding)"
                                    value={entityInput}
                                    onChange={(ev: any) => setEntityInput(ev.target.value)}
                                    onKeyDown={(ev: any) => { if (ev.key === 'Enter') applyFilter(); }}
                                />
                            </div>
                            <button onClick={applyFilter}
                                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                title="Aplicar filtro">
                                Filtrar
                            </button>
                            {hasActiveFilter && (
                                <button onClick={clearFilter}
                                    className="px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium"
                                    title="Limpar filtro">
                                    Limpar
                                </button>
                            )}
                            <button onClick={onExport} disabled={!hasData}
                                className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                title="Exportar CSV">
                                <FiDownload size={14} /> Exportar
                            </button>
                            <button onClick={r.refresh}
                                className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Atualizar">
                                <IoIosRefresh size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-4 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <SummaryCard label="Casos" value={String(report.summary.count)} />
                        <SummaryCard label="Perda estimada total" value={report.summary.estimatedLossFormatted || 'R$ 0,00'} />
                        <SummaryCard label="Provisão total" value={report.summary.provisionFormatted || 'R$ 0,00'} accent="danger" />
                        <SummaryCard
                            label="% fora do padrão sugerido"
                            value={String(report.summary.offSuggestion)}
                            hint="Casos cujo percentual de perda difere do sugerido para a classificação. Não é erro — vale conferir se foi intencional."
                        />
                        <SummaryCard
                            label="Provisão ajustada manualmente"
                            value={String(report.summary.overridden)}
                            hint="Casos onde a provisão foi sobrescrita à mão, em vez do valor calculado pela regra."
                        />
                    </div>

                    {hasData && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Cpc25BarChart
                                title="Provisão por entidade credora"
                                segments={provisionSegments}
                                emptyMessage="Nenhuma provisão reconhecida (só passivo provável provisiona)."
                            />
                            <Cpc25DonutChart
                                title="Casos por classificação"
                                segments={classificationSegments}
                            />
                        </div>
                    )}

                    <AttentionSection cases={report.attention} onOpen={(id) => navigate(`/recovery/legal-cases/${id}`)} />

                    {!hasData ? (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                            Nenhum caso para consolidar.
                        </div>
                    ) : (
                        report.entities.map((entity) => (
                            <EntityBlock key={entity.creditorEntity} entity={entity} />
                        ))
                    )}

                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                        Provisão reconhecida apenas para casos passivos classificados como prováveis (CPC 25).
                        Ativo contingente não é provisionado. Os valores são calculados a partir dos casos cadastrados.
                    </p>
                </div>
            </div>
        </>
    );
};

export default Cpc25ReportPage;
